import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Building2, RefreshCw, Search } from "lucide-react";

import {
  ConfirmDialog,
  PageHeader,
  PaginationControls,
} from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTran } from "@/hooks";
import { INDUSTRIAL_CLASSIFICATION, LOCATION_OPTIONS } from "@/lib/constants";
import { getOptionLabel } from "@/lib/utils";
import { useCompanyStore } from "../hooks";
import CompanyActions from "../components/CompanyActions";
import CompanySetupDialog from "../components/CompanySetupDialog";
import { companyService } from "../services/company.service";
import { showToast } from "@/lib/utils/toast";

const getCompanyId = (company) =>
  company?.id ?? company?._id ?? company?.companyId ?? "";

const formatListCount = (count) =>
  `${count.toLocaleString()} ${count === 1 ? "company" : "companies"}`;

const CompanyListPage = observer(() => {
  const { t } = useTran();
  const {
    adminCompanies: companies,
    adminCompaniesLoading: companiesLoading,
    adminCompaniesError: companiesError,
    adminCompaniesMeta,
    fetchCompanies,
    fetchPublicCompanies,
    removeCompanyFromCache,
    upsertCompany,
  } = useCompanyStore();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchCompanies({ force: true, page, limit: pageSize, search }).catch(
      () => {},
    );
  }, [fetchCompanies, page, pageSize, search]);

  const totalCompanies = Number(adminCompaniesMeta?.total || 0);
  const noCompanies = !Array.isArray(companies) || companies.length === 0;

  const handleRefresh = () => {
    fetchCompanies({ force: true, page, limit: pageSize, search }).catch(
      () => {},
    );
  };

  const openDeleteDialog = (company) => {
    setCompanyToDelete(company);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setCompanyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    const companyId = getCompanyId(companyToDelete);
    if (!companyId) {
      closeDeleteDialog();
      return;
    }

    setIsDeleting(true);
    try {
      await companyService.updateCompany(companyId, { status: "deleted" });
      removeCompanyFromCache(companyId);
      await Promise.all([
        fetchCompanies({ force: true, page, limit: pageSize, search }),
        fetchPublicCompanies({ force: true }),
      ]);
      showToast.success(t("Company deleted successfully."));
    } catch (error) {
      showToast.error(error?.message || t("Unable to delete company."));
    } finally {
      setIsDeleting(false);
      setCompanyToDelete(null);
    }
  };

  const handleCompleted = (company) => {
    if (company) upsertCompany(company);
    fetchCompanies({ force: true, page, limit: pageSize, search }).catch(
      () => {},
    );
    setEditingCompany(null);
  };

  const editingCompanyId = getCompanyId(editingCompany);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={t("Companies")}
        description={t("View and update company information for all tenants.")}
        icon={Building2}
        actions={
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={companiesLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${companiesLoading ? "animate-spin" : ""}`}
            />
            {t("Refresh")}
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("Company Directory")}
              </p>
              <p className="text-xs text-muted-foreground">
                {companiesLoading
                  ? t("Loading companies...")
                  : formatListCount(totalCompanies)}
              </p>
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => {
                  setPage(1);
                  setSearch(event.target.value);
                }}
                placeholder={t("Search companies...")}
                className="pl-9"
              />
            </div>
          </div>

          {companiesError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {companiesError}
            </div>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Company")}</TableHead>
                <TableHead>{t("Country")}</TableHead>
                <TableHead>{t("Industry")}</TableHead>
                <TableHead>{t("Employees")}</TableHead>
                <TableHead>{t("Target Frequency")}</TableHead>
                <TableHead className="w-24 text-right">
                  {t("Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companiesLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6}>
                      <div className="h-6 animate-pulse rounded bg-muted" />
                    </TableCell>
                  </TableRow>
                ))
              ) : noCompanies ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-32 text-center text-muted-foreground"
                  >
                    {t("No companies found.")}
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => {
                  const companyId = getCompanyId(company);
                  const companyName =
                    company.companyName || company.name || t("Company");
                  const country = getOptionLabel(
                    company.country,
                    LOCATION_OPTIONS,
                    company.country || "-",
                  );
                  const industry = getOptionLabel(
                    company.industry,
                    INDUSTRIAL_CLASSIFICATION,
                    company.industry || "-",
                  );
                  const targetFrequency =
                    company.targetFrequency === "monthly"
                      ? t("Monthly")
                      : t("Yearly");

                  return (
                    <TableRow key={companyId || companyName}>
                      <TableCell>
                        <div className="min-w-48">
                          <p className="font-medium text-foreground">
                            {companyName}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{country}</TableCell>
                      <TableCell>
                        <span className="line-clamp-2 max-w-72 whitespace-normal">
                          {industry}
                        </span>
                      </TableCell>
                      <TableCell>{company.employeesRange || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{targetFrequency}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <CompanyActions
                          company={company}
                          onEdit={() => setEditingCompany(company)}
                          onDelete={openDeleteDialog}
                          disabled={!companyId}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <div className="mt-4">
            <PaginationControls
              page={page}
              pageSize={pageSize}
              total={adminCompaniesMeta?.total ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
              pageSizeOptions={[10, 30, 50]}
              disabled={companiesLoading}
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(companyToDelete)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeDeleteDialog();
        }}
        title={t("Delete company")}
        description={t(
          "This will mark the company as deleted. Deleted companies are removed from the list.",
        )}
        tone="danger"
        confirmLabel={isDeleting ? t("Deleting...") : t("Delete")}
        cancelLabel={t("Cancel")}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      <CompanySetupDialog
        open={Boolean(editingCompanyId)}
        user={editingCompanyId ? { companyId: editingCompanyId } : null}
        showTargetFrequency
        onClose={() => setEditingCompany(null)}
        onCompleted={handleCompleted}
      />
    </div>
  );
});

export default CompanyListPage;
